/**
 * Gedeelde grenzen en toegestane types voor klantuploads.
 *
 * Waarom een apart bestand: de grens stond eerder op drie plaatsen los van
 * elkaar (route, browservalidatie en de teksten in vier talen). Toen Vercel
 * elke aanvraag boven ~4,5 MB met een 413 weigerde, beloofde de UI nog steeds
 * 100 MB. Een klant kon dus niets doen aan een fout die wij zelf veroorzaakten.
 * Alles wat een grens noemt leest hier.
 *
 * Dit bestand mag geen server-only imports bevatten; het wordt ook in de
 * browser gebundeld.
 */

/** Types die we accepteren. Identiek voor de klassieke route en de tokenroute. */
export const ALLOWED_UPLOAD_CONTENT_TYPES = [
  'application/pdf',
  // Grote editorstaten gaan als JSON naar Blob, niet via de bestandskiezer.
  'application/json',
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
  // Vectorbestanden. EPS, PS en niet-PDF-compatibele AI komen binnen als
  // PostScript; PDF-compatibele AI gaat als gewone PDF. Het type wordt bij
  // deze twee nooit van de browser overgenomen maar uit naam en inhoud
  // bepaald, zie src/lib/uploads/artwork-formats.ts.
  'application/postscript',
  'image/svg+xml',
] as const;

/**
 * Hardgrens per bestand: 500 MB.
 *
 * Afweging: Vercel Blob zelf gaat tot 5 TB, dus het platform is niet de rem.
 * De echte rem is het tabblad van de klant. De designer bouwt printbestanden
 * op als data-URL (base64), en base64 is ~1,33x de bytes; een bestand van
 * 500 MB betekent dus al honderden MB's aan string in het geheugen voordat wij
 * iets doen. Boven 500 MB loopt een gemiddelde browser leeg voordat de upload
 * begint. 500 MB dekt ruimschoots elk realistisch drukbestand (grootformaat
 * PDF met afbeeldingen op 300 dpi) en houdt het tabblad heel.
 *
 * Deze grens wordt afgedwongen in de tokenroute (`maximumSizeInBytes`), niet
 * alleen in de browser: het clienttoken is wat Vercel Blob controleert, dus dat
 * is de enige plek die een klant niet kan omzeilen.
 */
export const MAX_UPLOAD_BYTES = 500 * 1024 * 1024;

/** Label voor klantteksten. Eén plek, zodat de vier talen niet uiteenlopen. */
export const MAX_UPLOAD_LABEL = '500 MB';

/**
 * Boven deze grootte gaat de upload rechtstreeks van de browser naar Vercel
 * Blob in plaats van door onze eigen functie.
 *
 * 4 MB, want de body van een Vercel Serverless Function wordt rond 4,5 MB door
 * het platform geweigerd (413, de route draait dan niet eens). Multipart
 * form-encoding voegt daar nog wat overhead aan toe, dus 4 MB houdt marge.
 * Daaronder blijft de bestaande route ongewijzigd in gebruik: dat is één
 * rondgang minder en het is het pad dat al maanden werkt.
 */
export const DIRECT_UPLOAD_THRESHOLD_BYTES = 4 * 1024 * 1024;

/**
 * Boven deze grootte wordt de directe upload in delen verstuurd.
 *
 * Multipart splitst het bestand in blokken van minimaal 5 MB, stuurt ze
 * parallel en probeert een mislukt blok opnieuw. Dat is precies wat een
 * bestand van honderden MB's over een huislijn overeind houdt. Onder 8 MB is
 * één PUT eenvoudiger en even betrouwbaar.
 */
export const MULTIPART_UPLOAD_THRESHOLD_BYTES = 8 * 1024 * 1024;

/**
 * Na zoveel milliseconden zonder ook maar één voortgangsmelding breken we de
 * rechtstreekse upload af.
 *
 * Waarom dit moet: de SDK probeert een mislukt blok in stilte opnieuw en blijft
 * dat doen. Gemeten met een doorgeknipte verbinding liep hij na 205 seconden
 * nog steeds door zonder de belofte te verwerpen. De klant zag dan een balk die
 * niet meer bewoog en een opslaanknop die uit stond: geen fout, geen uitweg.
 * Met deze grens wordt het een echte fout die de klant kan herhalen.
 *
 * 120 seconden is ruim: bij 150 MB kwam er gemiddeld elke 0,6 seconde een
 * melding binnen. Twee minuten helemaal niets is geen trage lijn meer.
 */
export const UPLOAD_STALL_ABORT_MS = 120 * 1000;

/** Normaliseert `image/PNG; charset=x` naar `image/png` voor de vergelijking. */
export function normalizeContentType(type: string | null | undefined): string {
  if (!type) return '';
  return type.split(';')[0].trim().toLowerCase();
}

export function isAllowedUploadContentType(type: string | null | undefined): boolean {
  const normalized = normalizeContentType(type);
  return (ALLOWED_UPLOAD_CONTENT_TYPES as readonly string[]).includes(normalized);
}

/**
 * Maakt van een bestandsnaam een veilig pad binnen de blobstore.
 * De klant bepaalt de naam, dus mappen, `..` en rare tekens gaan eruit.
 */
export function sanitizeUploadFileName(fileName: string): string {
  const base = String(fileName || '')
    .split(/[\\/]/)
    .pop() || '';
  const cleaned = base
    .replace(/[^a-zA-Z0-9._-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^[-.]+/, '')
    .slice(0, 120);
  return cleaned || `upload-${Date.now()}.bin`;
}

/**
 * Controleert of een pad dat de browser vraagt acceptabel is om een token voor
 * af te geven. Bewust streng: het token geeft schrijfrecht op dat pad.
 */
export function isSafeUploadPathname(pathname: string): boolean {
  if (!pathname || typeof pathname !== 'string') return false;
  if (pathname.length > 200) return false;
  if (pathname.startsWith('/')) return false;
  if (pathname.includes('..')) return false;
  if (pathname.includes('\\')) return false;
  return /^[a-zA-Z0-9._\-/]+$/.test(pathname);
}

/**
 * Alleen URL's van onze eigen blobstore mogen als `blobUrl` in de database.
 * Zonder deze controle zou /api/upload/register een willekeurige URL als
 * bestelbestand kunnen laten vastleggen.
 */
export function isVercelBlobUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'https:') return false;
    return parsed.hostname.endsWith('.vercel-storage.com');
  } catch {
    return false;
  }
}
